import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  MessageSquare, 
  Phone, 
  Clock, 
  CheckCircle2, 
  AlertTriangle,
  Send,
  User,
  Bot,
  ArrowLeft,
  MoreVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger 
} from '../components/ui/dropdown-menu';
import { 
  getConversations, 
  getConversation, 
  getConversationMessages,
  updateConversationStatus,
  testWebhook
} from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export const ConversationsPage = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [sending, setSending] = useState(false);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const loadConversations = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getConversations();
      setConversations(res.data);
    } catch (error) {
      // Production: error logged silently
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConversationDetails = useCallback(async (id) => {
    try {
      setMessagesLoading(true);
      const [convRes, messagesRes] = await Promise.all([
        getConversation(id),
        getConversationMessages(id)
      ]);
      setSelectedConversation(convRes.data);
      setMessages(messagesRes.data);
    } catch (error) {
      // Production: error logged silently
      toast.error('Failed to load conversation');
      navigate('/conversations');
    } finally {
      setMessagesLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    if (conversationId) {
      loadConversationDetails(conversationId);
    } else {
      setSelectedConversation(null);
      setMessages([]);
    }
  }, [conversationId, loadConversationDetails]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleStatusChange = async (status) => {
    if (!selectedConversation) return;
    try {
      await updateConversationStatus(selectedConversation.id, status);
      setSelectedConversation(prev => ({ ...prev, status }));
      toast.success(`Conversation marked as ${status}`);
      loadConversations();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleSendTestMessage = async (e) => {
    e.preventDefault();
    if (!testMessage.trim()) return;
    
    try {
      setSending(true);
      const res = await testWebhook({
        phone: selectedConversation?.phone_number || '1234567890',
        message: testMessage,
        name: 'Test User'
      });
      
      toast.success('Message processed successfully');
      setTestMessage('');
      
      // Reload conversation if we have one selected
      if (selectedConversation) {
        loadConversationDetails(selectedConversation.id);
      } else {
        // Navigate to the new conversation
        navigate(`/conversations/${res.data.conversation_id}`);
      }
      loadConversations();
    } catch (error) {
      toast.error('Failed to send test message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-emerald-500/10 text-emerald-500',
      resolved: 'bg-zinc-500/10 text-zinc-400',
      escalated: 'bg-amber-500/10 text-amber-500'
    };
    const icons = {
      active: MessageSquare,
      resolved: CheckCircle2,
      escalated: AlertTriangle
    };
    const Icon = icons[status] || MessageSquare;
    
    return (
      <Badge className={cn("gap-1", styles[status])}>
        <Icon className="w-3 h-3" />
        {status}
      </Badge>
    );
  };

  return (
    <div className="flex h-[calc(100vh-4rem)]" data-testid="conversations-page">
      {/* Conversations List */}
      <div className={cn(
        "w-full md:w-80 lg:w-96 border-r border-border flex flex-col bg-card/30",
        conversationId && "hidden md:flex"
      )}>
        <div className="p-4 border-b border-border">
          <h2 className="text-lg font-semibold">Conversations</h2>
          <p className="text-sm text-muted-foreground">
            {conversations.length} total
          </p>
        </div>
        
        <ScrollArea className="flex-1">
          {loading ? (
            <div className="p-4 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-16 skeleton rounded-lg" />
              ))}
            </div>
          ) : conversations.length > 0 ? (
            <div className="p-2">
              {conversations.map((conv) => (
                <div
                  key={conv.id}
                  className={cn(
                    "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors",
                    "hover:bg-muted/30",
                    conversationId === conv.id && "bg-primary/10 border-l-2 border-l-primary"
                  )}
                  onClick={() => navigate(`/conversations/${conv.id}`)}
                  data-testid={`conv-item-${conv.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium truncate">
                        {conv.customer_name || 'Unknown'}
                      </p>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {formatDate(conv.last_message_at)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-1">
                      <p className="text-sm text-muted-foreground truncate font-mono">
                        {conv.phone_number}
                      </p>
                      {getStatusBadge(conv.status)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center p-4">
              <MessageSquare className="w-12 h-12 text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground">No conversations yet</p>
              <p className="text-sm text-muted-foreground/70 mt-1">
                Test the bot below to create one
              </p>
            </div>
          )}
        </ScrollArea>

        {/* Test Message Input */}
        <div className="p-4 border-t border-border bg-card/50">
          <form onSubmit={handleSendTestMessage} className="space-y-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              Test Bot
            </p>
            <div className="flex gap-2">
              <Input
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Type a test message..."
                className="flex-1 bg-background/50"
                data-testid="test-message-input"
              />
              <Button 
                type="submit" 
                size="icon"
                disabled={sending || !testMessage.trim()}
                className="bg-primary text-primary-foreground"
                data-testid="send-test-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </form>
        </div>
      </div>

      {/* Chat View */}
      <div className={cn(
        "flex-1 flex flex-col",
        !conversationId && "hidden md:flex"
      )}>
        {selectedConversation ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-border bg-card/30 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden"
                  onClick={() => navigate('/conversations')}
                  data-testid="back-btn"
                >
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                  <User className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-semibold">
                    {selectedConversation.customer_name || 'Unknown User'}
                  </p>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="w-3 h-3" />
                    <span className="font-mono">{selectedConversation.phone_number}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedConversation.status)}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" data-testid="conv-actions">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleStatusChange('active')}>
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Mark Active
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleStatusChange('resolved')}>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Mark Resolved
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleStatusChange('escalated')}>
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Escalate
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Messages */}
            <ScrollArea className="flex-1 chat-container">
              <div className="p-4 space-y-4">
                {messagesLoading ? (
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className={cn(
                        "h-16 skeleton rounded-2xl max-w-[70%]",
                        i % 2 === 0 ? "ml-auto" : ""
                      )} />
                    ))}
                  </div>
                ) : messages.length > 0 ? (
                  messages.map((msg, index) => (
                    <div
                      key={msg.id}
                      className={cn(
                        "flex gap-2 message-bubble",
                        msg.sender_type === 'user' ? 'justify-end' : 'justify-start'
                      )}
                      data-testid={`message-${msg.id}`}
                    >
                      {msg.sender_type === 'bot' && (
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-auto">
                          <Bot className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <div className={cn(
                        "max-w-[70%] p-3 rounded-2xl",
                        msg.sender_type === 'user' 
                          ? 'chat-bubble-user' 
                          : 'chat-bubble-bot'
                      )}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                        <p className="text-xs text-muted-foreground mt-1 text-right">
                          {formatTime(msg.created_at)}
                        </p>
                      </div>
                      {msg.sender_type === 'user' && (
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0 mt-auto">
                          <User className="w-4 h-4" />
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center justify-center h-64 text-center">
                    <MessageSquare className="w-12 h-12 text-muted-foreground/50 mb-4" />
                    <p className="text-muted-foreground">No messages yet</p>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Simulated Input (read-only for admin) */}
            <div className="p-4 border-t border-border bg-card/30">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="w-4 h-4" />
                <span>
                  Conversations are handled automatically by the AI bot. 
                  Use the test input in the sidebar to simulate messages.
                </span>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <MessageSquare className="w-10 h-10 text-primary" />
            </div>
            <h3 className="text-xl font-semibold">Select a Conversation</h3>
            <p className="text-muted-foreground mt-2 max-w-md">
              Choose a conversation from the list to view messages, or use the test input to simulate a new conversation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
